import React, { useEffect, useRef, useState } from 'react';
import { getDisplayName } from '../utils/displayName';
import { kundedomene } from '../kundesider';
import { useSideTittel } from '../components/tilgjengelighet';
import { SYKKEL_NOKKEL } from './SykkelTilMinSide';
import { useSmsKode } from '../utils/smsKode';
import { TILSTAND_LABEL, settTekst } from '../constants/dekkhotell';
import { STATUS_KORT, STATUS_CLS, STED_LABEL } from '../constants/sykkelhotell';
import DineData from './minSide/DineData';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TOKEN_KEY = 'customer_portal_token';
const BOOKING_URL = 'https://www.roykensykkelservice.no';

// «Book» på et sykkelkort: bookingsiden fylles ut med kunden og sykkelen. Opplysningene
// ligger i #-fragmentet, som aldri sendes til en server eller til analyse (P1).
// Bookingsiden leser og fjerner det med en gang (royken-sykkelservice, Layout.astro).
function bookingLenke(bike, customer) {
  const elsykkel = typeof bike.elsykkel === 'boolean' ? bike.elsykkel : /^E-/.test(bike.type || '') ? true : null;
  const data = {
    v: 1,
    kunde: {
      fornavn: customer?.firstName || '',
      etternavn: customer?.lastName || '',
      telefon: customer?.phoneNumber || '',
      epost: customer?.email || '',
    },
    sykkel: {
      merke: bike.merke || '',
      modell: bike.modell || '',
      type: TYPE_LABELS[bike.type] || bike.type || '',
      elsykkel,
      token: bike.token || '',
    },
  };
  try {
    const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(data))))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return `${BOOKING_URL}/sykkelservice#fra-min-side=${b64}`;
  } catch {
    return `${BOOKING_URL}/sykkelservice`;
  }
}

const TYPE_LABELS = {
  landevei: 'Landevei', gravel: 'Gravel', MTB: 'MTB', FS: 'FS',
  Hybrid: 'Hybrid', barnesykkel: 'Barnesykkel',
  'E-MTB': 'E-MTB', 'E-FS': 'E-FS', 'E-trekking': 'E-trekking',
};

const STATUS_LABELS = {
  mottatt: 'Mottatt', under_arbeid: 'Under arbeid',
  'under arbeid': 'Under arbeid', venter: 'Venter',
  ferdig: 'Ferdig', avsluttet: 'Avsluttet', kansellert: 'Kansellert',
};

const STATUS_COLORS = {
  mottatt: 'bg-blue-100 text-blue-800',
  under_arbeid: 'bg-yellow-100 text-yellow-800',
  'under arbeid': 'bg-yellow-100 text-yellow-800',
  venter: 'bg-orange-100 text-orange-800',
  ferdig: 'bg-green-100 text-green-800',
  avsluttet: 'bg-gray-100 text-gray-600',
  kansellert: 'bg-red-100 text-red-700',
};

const fmtDate = (d) => {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';
};

function serviceDesc(svc) {
  const lines = Array.isArray(svc.serviceLines) ? svc.serviceLines.map((l) => l.navn).filter(Boolean) : [];
  return lines.length ? lines.join(', ') : '';
}

function serviceTotal(svc) {
  const svcSum = (svc.serviceLines || []).reduce((s, l) => (l.reklamasjon || l.gratisarbeid ? s : s + (Number(l.pris) || 0)), 0);
  const prtSum = (svc.partsLines || []).reduce((s, l) => {
    if (l.isWarranty) return s;
    return s + Number(l.price ?? l.pris ?? 0) * Number(l.qty ?? l.antall ?? 1) - Number(l.discount ?? 0);
  }, 0);
  return svcSum + prtSum;
}

function fmtNOK(n) {
  return new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(n);
}

// ─── Steg 1: Skriv inn telefon / e-post ──────────────────────────────────────

function RequestPinStep({ onSuccess, fokus }) {
  const [identifier, setIdentifier] = useState('');
  // Kom kunden fra QR-koden på en sykkel (SykkelTilMinSide.jsx)?
  const [fraSykkel] = useState(() => {
    try { return !!sessionStorage.getItem(SYKKEL_NOKKEL); } catch { return false; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const feltRef = useRef(null);

  // Fokus på feltet når kunden kommer hit fra et annet steg (ikke ved første lasting)
  useEffect(() => {
    if (fokus) feltRef.current?.focus();
  }, [fokus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/customer-portal/request-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Noe gikk galt');
      onSuccess({ ...data, identifier: identifier.trim() });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-6">
      <div className="text-3xl mb-3 text-center" aria-hidden="true">🔑</div>
      <h2 className="text-lg font-bold text-gray-900 text-center mb-1">Logg inn</h2>
      <p className="text-sm text-gray-600 text-center mb-6">
        {fraSykkel
          ? 'Sykkelkortet ligger på Din side. Skriv inn telefonnummer eller e-post for å motta en engangskode.'
          : 'Skriv inn telefonnummer eller e-post for å motta en engangskode.'}
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <label htmlFor="minside-identifikator" className="block text-sm font-medium text-gray-700 -mb-2">
          Telefon eller e-post
        </label>
        <input
          ref={feltRef}
          id="minside-identifikator"
          type="text"
          // Vanlig tastatur (ikke talltastatur), så både mobilnummer og e-post kan skrives.
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'minside-identifikator-feil' : undefined}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-700"
          required
        />
        {error && <p id="minside-identifikator-feil" className="text-red-700 text-sm" role="alert">{error}</p>}
        <button
          type="submit"
          disabled={loading || !identifier.trim()}
          className="w-full bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl py-3 text-sm transition-colors disabled:opacity-50"
        >
          {loading ? 'Sender kode…' : 'Send innloggingskode'}
        </button>
      </form>
    </div>
  );
}

// ─── Steg 2: Skriv inn PIN ────────────────────────────────────────────────────
function VerifyPinStep({ identifier, customerId, method, masked, onSuccess, onBack }) {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const feltRef = useRef(null);

  // Kunden kommer alltid hit fra steg 1, så kodefeltet får fokus
  useEffect(() => {
    feltRef.current?.focus();
  }, []);

  const loggInnRef = useRef(null);
  useSmsKode(method === 'sms', (kode) => {
    setPin(kode);
    setTimeout(() => loggInnRef.current?.focus(), 0);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/customer-portal/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // customerId sendes bare av den gamle backenden (fjernes når den nye er ute).
        body: JSON.stringify({ identifier, customerId, pin }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Feil kode');
      onSuccess(data.token);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-6">
      <div className="text-3xl mb-3 text-center" aria-hidden="true">📱</div>
      <h2 className="text-lg font-bold text-gray-900 text-center mb-1">Skriv inn koden</h2>
      <p id="minside-kode-hjelp" className="text-sm text-gray-600 text-center mb-6">
        Er <span className="font-medium text-gray-700">{masked}</span> registrert hos oss, har vi sendt
        en 6-sifret kode via {method === 'sms' ? 'SMS' : 'e-post'}. Kommer den ikke, kan du ta kontakt
        med oss.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <label htmlFor="minside-kode" className="block text-sm font-medium text-gray-700 text-center -mb-2">
          Engangskode
        </label>
        <input
          ref={feltRef}
          id="minside-kode"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          aria-describedby={error ? 'minside-kode-hjelp minside-kode-feil' : 'minside-kode-hjelp'}
          aria-invalid={error ? true : undefined}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          className="w-full border border-gray-300 rounded-xl px-4 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-green-700"
          required
        />
        {error && <p id="minside-kode-feil" className="text-red-700 text-sm text-center" role="alert">{error}</p>}
        <button
          ref={loggInnRef}
          type="submit"
          disabled={loading || pin.length !== 6}
          className="w-full bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl py-3 text-sm transition-colors disabled:opacity-50"
        >
          {loading ? 'Sjekker…' : 'Logg inn'}
        </button>
        <button type="button" onClick={onBack} className="w-full text-sm text-gray-600 hover:text-gray-900 py-1">
          <span aria-hidden="true">← </span>Tilbake
        </button>
      </form>
    </div>
  );
}

// ─── Mine opplysninger (kundeflyt fase 3) ─────────────────────────────────────
// Navn endres direkte. E-post og telefon bekreftes med en kode til den nye
// adressen eller det nye nummeret (src/routes/customerPortal.js).
const FELT_CLS = 'w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-700';
const KNAPP_CLS = 'bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl px-4 py-2 text-sm disabled:opacity-50';
const SEKUNDAER_CLS = 'text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-xl px-4 py-2';

async function portalKall(token, metode, sti, body) {
  const r = await fetch(`${API_BASE_URL}/customer-portal${sti}`, {
    method: metode,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.message || 'Noe gikk galt'), { status: r.status });
  return data;
}

function MineOpplysninger({ token, customer, onOppdatert, onLoggUt }) {
  // redigerer: null | 'navn' | 'email' | 'phoneNumber'
  const [redigerer, setRedigerer] = useState(null);
  const [fornavn, setFornavn] = useState('');
  const [etternavn, setEtternavn] = useState('');
  const [verdi, setVerdi] = useState('');
  const [kode, setKode] = useState('');
  const [kodeSendtTil, setKodeSendtTil] = useState('');
  const [opptatt, setOpptatt] = useState(false);
  const [feil, setFeil] = useState('');
  const [status, setStatus] = useState('');
  const forsteFeltRef = useRef(null);
  const kodeRef = useRef(null);
  const knappRefs = { navn: useRef(null), email: useRef(null), phoneNumber: useRef(null) };

  useEffect(() => {
    if (redigerer) forsteFeltRef.current?.focus();
  }, [redigerer]);
  useEffect(() => {
    if (kodeSendtTil) kodeRef.current?.focus();
  }, [kodeSendtTil]);

  const apne = (felt) => {
    setFeil('');
    setStatus('');
    setKode('');
    setKodeSendtTil('');
    if (felt === 'navn') {
      setFornavn(customer.firstName || '');
      setEtternavn(customer.lastName || '');
    } else {
      setVerdi('');
    }
    setRedigerer(felt);
  };
  const lukk = (melding = '') => {
    const felt = redigerer;
    setRedigerer(null);
    setKodeSendtTil('');
    setFeil('');
    setStatus(melding);
    setTimeout(() => knappRefs[felt]?.current?.focus(), 0);
  };

  const lagreNavn = async (e) => {
    e.preventDefault();
    setFeil('');
    setOpptatt(true);
    try {
      const data = await portalKall(token, 'PATCH', '/me/navn', { firstName: fornavn, lastName: etternavn });
      onOppdatert(data.customer);
      lukk('Navnet er lagret.');
    } catch (err) {
      setFeil(err.message);
    } finally {
      setOpptatt(false);
    }
  };

  const sendKode = async (e) => {
    e.preventDefault();
    setFeil('');
    setOpptatt(true);
    try {
      const data = await portalKall(token, 'POST', '/me/kontakt', { felt: redigerer, verdi });
      setKodeSendtTil(data.masked || verdi);
    } catch (err) {
      setFeil(err.message);
    } finally {
      setOpptatt(false);
    }
  };

  const bekreftRef = useRef(null);
  useSmsKode(redigerer === 'phoneNumber' && !!kodeSendtTil, (k) => {
    setKode(k);
    setTimeout(() => bekreftRef.current?.focus(), 0);
  });

  const bekreft = async (e) => {
    e.preventDefault();
    setFeil('');
    setOpptatt(true);
    try {
      const data = await portalKall(token, 'POST', '/me/kontakt/bekreft', { kode });
      if (data.loggUt) {
        onLoggUt('Telefonnummeret er endret. Logg inn med det nye nummeret.');
        return;
      }
      onOppdatert(data.customer);
      lukk('E-postadressen er endret.');
    } catch (err) {
      setFeil(err.message);
    } finally {
      setOpptatt(false);
    }
  };

  const navn = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
  const rader = [
    { felt: 'navn', etikett: 'Navn', vis: navn || 'Ikke registrert' },
    { felt: 'phoneNumber', etikett: 'Mobil', vis: customer.phoneNumber || 'Ikke registrert' },
    { felt: 'email', etikett: 'E-post', vis: customer.email || 'Ikke registrert' },
  ];
  const feilId = 'minside-opplysninger-feil';

  return (
    <section aria-labelledby="minside-opplysninger-tittel">
      <h2 id="minside-opplysninger-tittel" className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 px-1">
        Mine opplysninger
      </h2>
      <div className="bg-white rounded-2xl border shadow-sm divide-y">
        {rader.map((r) => (
          <div key={r.felt} className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs text-gray-600">{r.etikett}</div>
                <div className="text-sm text-gray-900 break-words">{r.vis}</div>
              </div>
              {redigerer !== r.felt && (
                <button
                  ref={knappRefs[r.felt]}
                  type="button"
                  onClick={() => apne(r.felt)}
                  className="text-sm text-green-800 hover:text-green-900 font-medium underline"
                >
                  Endre<span className="sr-only"> {r.etikett.toLowerCase()}</span>
                </button>
              )}
            </div>

            {redigerer === 'navn' && r.felt === 'navn' && (
              <form onSubmit={lagreNavn} className="mt-3 space-y-3">
                <div>
                  <label htmlFor="minside-fornavn" className="block text-sm font-medium text-gray-700 mb-1">Fornavn</label>
                  <input ref={forsteFeltRef} id="minside-fornavn" autoComplete="given-name" value={fornavn}
                    onChange={(e) => setFornavn(e.target.value)} className={FELT_CLS} required maxLength={100}
                    aria-invalid={feil ? true : undefined} aria-describedby={feil ? feilId : undefined} />
                </div>
                <div>
                  <label htmlFor="minside-etternavn" className="block text-sm font-medium text-gray-700 mb-1">Etternavn</label>
                  <input id="minside-etternavn" autoComplete="family-name" value={etternavn}
                    onChange={(e) => setEtternavn(e.target.value)} className={FELT_CLS} maxLength={100} />
                </div>
                {feil && <p id={feilId} className="text-red-700 text-sm" role="alert">{feil}</p>}
                <div className="flex flex-wrap gap-2">
                  <button type="submit" disabled={opptatt || !fornavn.trim()} className={KNAPP_CLS}>
                    {opptatt ? 'Lagrer…' : 'Lagre'}
                  </button>
                  <button type="button" onClick={() => lukk()} className={SEKUNDAER_CLS}>Avbryt</button>
                </div>
              </form>
            )}

            {redigerer === r.felt && r.felt !== 'navn' && !kodeSendtTil && (
              <form onSubmit={sendKode} className="mt-3 space-y-3">
                <div>
                  <label htmlFor="minside-ny-kontakt" className="block text-sm font-medium text-gray-700 mb-1">
                    {r.felt === 'email' ? 'Ny e-postadresse' : 'Nytt mobilnummer'}
                  </label>
                  <input ref={forsteFeltRef} id="minside-ny-kontakt"
                    type={r.felt === 'email' ? 'email' : 'tel'}
                    inputMode={r.felt === 'email' ? 'email' : 'tel'}
                    autoComplete={r.felt === 'email' ? 'email' : 'tel'}
                    value={verdi} onChange={(e) => setVerdi(e.target.value)} className={FELT_CLS} required maxLength={200}
                    aria-invalid={feil ? true : undefined}
                    aria-describedby={`minside-ny-kontakt-hjelp${feil ? ` ${feilId}` : ''}`} />
                  <p id="minside-ny-kontakt-hjelp" className="text-xs text-gray-600 mt-1">
                    {r.felt === 'email'
                      ? 'Vi sender en kode til den nye adressen. Meldinger fra oss går dit når du har bekreftet den.'
                      : 'Vi sender en kode til det nye nummeret. Når du har bekreftet det, blir du logget ut, og det gamle nummeret får beskjed.'}
                  </p>
                </div>
                {feil && <p id={feilId} className="text-red-700 text-sm" role="alert">{feil}</p>}
                <div className="flex flex-wrap gap-2">
                  <button type="submit" disabled={opptatt || !verdi.trim()} className={KNAPP_CLS}>
                    {opptatt ? 'Sender…' : 'Send kode'}
                  </button>
                  <button type="button" onClick={() => lukk()} className={SEKUNDAER_CLS}>Avbryt</button>
                </div>
              </form>
            )}

            {redigerer === r.felt && r.felt !== 'navn' && kodeSendtTil && (
              <form onSubmit={bekreft} className="mt-3 space-y-3">
                <div>
                  <label htmlFor="minside-endringskode" className="block text-sm font-medium text-gray-700 mb-1">Kode</label>
                  <input ref={kodeRef} id="minside-endringskode" type="text" inputMode="numeric" autoComplete="one-time-code"
                    maxLength={6} value={kode} onChange={(e) => setKode(e.target.value.replace(/\D/g, ''))}
                    className={`${FELT_CLS} font-mono tracking-[0.3em]`} required
                    aria-invalid={feil ? true : undefined}
                    aria-describedby={`minside-endringskode-hjelp${feil ? ` ${feilId}` : ''}`} />
                  <p id="minside-endringskode-hjelp" className="text-xs text-gray-600 mt-1" role="status">
                    Vi har sendt en 6-sifret kode til {kodeSendtTil}. Den er gyldig i 10 minutter.
                  </p>
                </div>
                {feil && <p id={feilId} className="text-red-700 text-sm" role="alert">{feil}</p>}
                <div className="flex flex-wrap gap-2">
                  <button ref={bekreftRef} type="submit" disabled={opptatt || kode.length !== 6} className={KNAPP_CLS}>
                    {opptatt ? 'Sjekker…' : 'Bekreft'}
                  </button>
                  <button type="button" onClick={() => lukk()} className={SEKUNDAER_CLS}>Avbryt</button>
                </div>
              </form>
            )}
          </div>
        ))}
      </div>
      <p className="text-sm text-gray-700 mt-2 px-1" role="status">{status}</p>
    </section>
  );
}

// ─── Servicepåminnelser på SMS ────────────────────────────────────────────────
// Kunden kan reservere seg mot servicepåminnelser (markedsføringsloven § 15, se
// samtykke-plan.md). Avkrysset = får påminnelser. Lagres med en gang.
function Servicepaminnelse({ token, verdi, onEndret }) {
  const [opptatt, setOpptatt] = useState(false);
  const [feil, setFeil] = useState('');
  const [status, setStatus] = useState('');

  // Boksen gjøres ikke disabled mens den lagrer: da mister tastaturbrukere fokus.
  const endre = async (e) => {
    if (opptatt) return;
    const ny = e.target.checked;
    setFeil('');
    setStatus('');
    setOpptatt(true);
    try {
      const data = await portalKall(token, 'PUT', '/me/servicepaminnelse', { verdi: ny });
      onEndret(data.servicepaminnelseSms);
      setStatus(ny ? 'Lagret. Du får påminnelse om service på SMS.' : 'Lagret. Du får ikke påminnelse om service.');
    } catch (err) {
      setFeil(err.message);
    } finally {
      setOpptatt(false);
    }
  };

  return (
    <section aria-labelledby="minside-varsler-tittel">
      <h2 id="minside-varsler-tittel" className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 px-1">
        SMS fra oss
      </h2>
      <div className="bg-white rounded-2xl border shadow-sm p-4">
        <label htmlFor="minside-servicepaminnelse" className="flex items-start gap-3 text-sm text-gray-900">
          <input
            id="minside-servicepaminnelse"
            type="checkbox"
            checked={verdi}
            aria-busy={opptatt || undefined}
            onChange={endre}
            aria-describedby="minside-servicepaminnelse-hjelp"
            className="w-5 h-5 mt-0.5 flex-shrink-0 accent-green-700"
          />
          <span>Påminnelse om service på SMS</span>
        </label>
        <p id="minside-servicepaminnelse-hjelp" className="text-xs text-gray-600 mt-1 ml-8">
          Vi minner deg på det når det er lenge siden forrige service. Beskjeder om bookinger og servicer du
          har hos oss, får du uansett.
        </p>
        {feil && <p className="text-red-700 text-sm mt-2" role="alert">{feil}</p>}
      </div>
      <p className="text-sm text-gray-700 mt-2 px-1" role="status">{status}</p>
    </section>
  );
}

// ─── Bike-kort (valgbart) ─────────────────────────────────────────────────────
function BikeCard({ bike, customer, selected, onToggle }) {
  const title = [bike.merke, bike.modell].filter(Boolean).join(' ') || 'Sykkel';
  const summary = [TYPE_LABELS[bike.type] || bike.type, bike.årsmodell].filter(Boolean).join(' · ');

  const hasExtra = bike.type || bike.årsmodell || bike.rammenummer;

  return (
    <div className={`bg-white rounded-xl border shadow-sm transition-all ${
      selected ? 'border-green-700 ring-1 ring-green-700' : 'border-gray-200'
    }`}>
      {/* Kompakt rad: knappen velger sykkelen (filtrerer historikken), lenken booker */}
      <div className="flex items-center gap-3 p-3">
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={selected}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left select-none"
        >
          <span aria-hidden="true" className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            selected ? 'bg-green-700 border-green-700' : 'border-gray-500'
          }`}>
            {selected && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </span>

          {bike.mainImageUrl ? (
            <img src={bike.mainImageUrl} alt=""
              className="w-14 h-14 object-cover rounded-lg border flex-shrink-0" />
          ) : (
            <span aria-hidden="true" className="w-14 h-14 bg-gray-100 rounded-lg border flex items-center justify-center text-2xl flex-shrink-0">
              🚲
            </span>
          )}

          <span className="block min-w-0 flex-1">
            <span className="block font-semibold text-gray-900 text-sm truncate">{title}</span>
            {summary && <span className="block text-xs text-gray-600 mt-0.5">{summary}</span>}
            {bike.shortId && (
              <span className="block font-mono text-xs text-gray-600 tracking-widest mt-0.5">#{bike.shortId}</span>
            )}
            <span className="sr-only">Vis servicehistorikk for denne sykkelen</span>
          </span>
        </button>

        <a
          href={bookingLenke(bike, customer)}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 bg-green-700 hover:bg-green-800 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors"
        >
          Book<span className="sr-only"> service for {title} (åpnes i ny fane)</span>
        </a>
      </div>

      {/* Utvidet info — vises når valgt */}
      {selected && hasExtra && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 pb-3 pt-2 border-t border-green-100 text-xs">
          {bike.type && (
            <>
              <dt className="text-gray-600">Type</dt>
              <dd className="text-gray-700 font-medium">{TYPE_LABELS[bike.type] || bike.type}</dd>
            </>
          )}
          {bike.årsmodell && (
            <>
              <dt className="text-gray-600">Årsmodell</dt>
              <dd className="text-gray-700 font-medium">{bike.årsmodell}</dd>
            </>
          )}
          {bike.rammenummer && (
            <>
              <dt className="text-gray-600">Rammenummer</dt>
              <dd className="text-gray-700 font-medium break-all">{bike.rammenummer}</dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}

// ─── Dekkhotell og sykkelhotell ───────────────────────────────────────────────
// Bare det vi oppbevarer nå (backend sender ikke avsluttede poster). Seksjonene
// vises bare når kunden har noe der.
function fmtIsoDato(iso) {
  const [y, m, d] = String(iso || '').split('-');
  return y && m && d ? `${d}.${m}.${y}` : '';
}

function HotellRad({ etikett, children }) {
  return (
    <>
      <dt className="text-gray-600">{etikett}</dt>
      <dd className="text-gray-800 font-medium break-words">{children}</dd>
    </>
  );
}

function HotellKort({ nr, tittel, merke, children }) {
  return (
    <li className="bg-white rounded-xl border shadow-sm p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <span className="block font-semibold text-gray-900 text-sm">{tittel}</span>
          <span className="block font-mono text-xs text-gray-600 tracking-widest mt-0.5">{nr}</span>
        </div>
        {merke}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">{children}</dl>
    </li>
  );
}

function Dekkhotell({ poster }) {
  return (
    <section aria-labelledby="minside-dekkhotell-tittel">
      <h2 id="minside-dekkhotell-tittel" className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 px-1">
        Dekkhotell ({poster.length})
      </h2>
      <ul className="space-y-2">
        {poster.map((d) => (
          <HotellKort key={d._id} nr={d.nr} tittel={d.sykkel || 'Dekk hos oss'}>
            <HotellRad etikett="Hos oss">{settTekst(d.lagret)}</HotellRad>
            {d.lagret?.tilstand && d.lagret.tilstand !== 'god' && (
              <HotellRad etikett="Tilstand">{TILSTAND_LABEL[d.lagret.tilstand] || d.lagret.tilstand}</HotellRad>
            )}
            {d.paSykkel && <HotellRad etikett="På sykkelen">{settTekst(d.paSykkel)}</HotellRad>}
            {fmtDate(d.lagretSiden) && <HotellRad etikett="Hos oss siden">{fmtDate(d.lagretSiden)}</HotellRad>}
            {fmtDate(d.betaltTil) && <HotellRad etikett="Betalt til">{fmtDate(d.betaltTil)}</HotellRad>}
          </HotellKort>
        ))}
      </ul>
    </section>
  );
}

function Sykkelhotell({ poster }) {
  return (
    <section aria-labelledby="minside-sykkelhotell-tittel">
      <h2 id="minside-sykkelhotell-tittel" className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 px-1">
        Sykkelhotell ({poster.length})
      </h2>
      <ul className="space-y-2">
        {poster.map((h) => {
          const sted = STED_LABEL[h.henting?.sted] || '';
          const dato = fmtIsoDato(h.henting?.dato);
          return (
            <HotellKort
              key={h._id}
              nr={h.nr}
              tittel={h.sykkel || 'Sykkel hos oss'}
              merke={(
                <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CLS[h.status] || 'bg-gray-100 text-gray-700'}`}>
                  {STATUS_KORT[h.status] || h.status}
                </span>
              )}
            >
              {h.sesong && <HotellRad etikett="Sesong">Vinteren {h.sesong}</HotellRad>}
              {h.status === 'innlevert' && <HotellRad etikett="Status">Levert inn, fraktes til lageret</HotellRad>}
              {h.status === 'henting-bestilt' && (dato || sted) && (
                <HotellRad etikett="Henting">{[dato, sted].filter(Boolean).join(', ')}</HotellRad>
              )}
              {h.status === 'klar' && sted && <HotellRad etikett="Hentes hos">{sted}</HotellRad>}
              {h.elsykkel && h.batteriMed && <HotellRad etikett="Batteri">Lagres sammen med sykkelen</HotellRad>}
              {h.medfolger && <HotellRad etikett="Fulgte med">{h.medfolger}</HotellRad>}
              {fmtDate(h.betaltTil) && <HotellRad etikett="Betalt til">{fmtDate(h.betaltTil)}</HotellRad>}
            </HotellKort>
          );
        })}
      </ul>
    </section>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ token, onLogout, fokus, visData, onNyInnlogging }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBikeIds, setSelectedBikeIds] = useState(new Set());
  const [expandedSvcIds, setExpandedSvcIds] = useState(new Set());
  const velkomstRef = useRef(null);

  // Rett etter innlogging: fokus på velkomsten, så skjermlesere starter øverst
  // (eller på «Dine data» når kunden kom fra lenken butikken sendte).
  useEffect(() => {
    if (fokus && data && !visData) velkomstRef.current?.focus();
  }, [fokus, data, visData]);

  const toggleReceipt = (id) => {
    setExpandedSvcIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/customer-portal/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error('Sesjonen er utløpt'); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Kom kunden hit fra QR-koden på en sykkel (/sykkel/<token>): vis den sykkelen.
  const [sykkelMelding, setSykkelMelding] = useState('');
  const sykkelRef = useRef(null);
  useEffect(() => {
    if (!data) return;
    let qr = null;
    try {
      qr = sessionStorage.getItem(SYKKEL_NOKKEL);
      sessionStorage.removeItem(SYKKEL_NOKKEL);
    } catch {
      return;
    }
    if (!qr) return;
    fetch(`${API_BASE_URL}/customer-portal/me/sykkel/${encodeURIComponent(qr)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((svar) => {
        if (svar?.bikeId) {
          setSelectedBikeIds(new Set([String(svar.bikeId)]));
          setSykkelMelding('Viser sykkelen du skannet.');
          setTimeout(() => sykkelRef.current?.focus(), 0);
        } else {
          setSykkelMelding('Sykkelen du skannet, er ikke knyttet til kontoen din. Ta kontakt med oss hvis den er din.');
        }
      })
      .catch(() => {});
  }, [data, token]);

  if (loading) return <p className="text-center text-gray-600 py-10" role="status">Laster…</p>;
  if (error) return (
    <div className="bg-white rounded-2xl border p-6 text-center">
      <p className="text-red-700 mb-4" role="alert">{error}</p>
      <button onClick={onLogout} className="text-sm text-blue-600 hover:underline">Logg inn på nytt</button>
    </div>
  );

  const { customer, bikes, history, dekkhotell = [], sykkelhotell = [] } = data;
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ');

  const toggleBike = (id) => {
    setSelectedBikeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredHistory = selectedBikeIds.size === 0
    ? history
    : history.filter((svc) => selectedBikeIds.has(String(svc.bikeId)));

  return (
    <div className="space-y-5">
      {/* Velkomst */}
      <div className="bg-white rounded-2xl border shadow-sm p-5 flex items-center justify-between">
        <h2 ref={velkomstRef} tabIndex={-1}>
          <span className="block text-xs text-gray-600 font-normal uppercase tracking-wide mb-0.5">Innlogget som</span>
          <span className="block font-bold text-gray-900 text-base">{name || customer.phoneNumber}</span>
        </h2>
        <button type="button" onClick={onLogout}
          className="text-xs text-gray-600 hover:text-red-700 border border-gray-200 rounded-lg px-3 py-1.5 transition-colors">
          Logg ut
        </button>
      </div>

      <MineOpplysninger
        token={token}
        customer={customer}
        onOppdatert={(c) => setData((d) => ({ ...d, customer: c }))}
        onLoggUt={onLogout}
      />

      <Servicepaminnelse
        token={token}
        verdi={data.servicepaminnelseSms !== false}
        onEndret={(v) => setData((d) => ({ ...d, servicepaminnelseSms: v }))}
      />

      {/* Mine sykler */}
      <section>
        {sykkelMelding && (
          <p className="bg-blue-50 border border-blue-200 text-blue-900 rounded-xl p-3 text-sm mb-2" role="status">
            {sykkelMelding}
          </p>
        )}
        <div className="flex items-center justify-between mb-2 px-1">
          <h2 ref={sykkelRef} tabIndex={-1} className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Mine sykler ({bikes.length})
          </h2>
          {selectedBikeIds.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedBikeIds(new Set())}
              className="text-xs text-green-700 hover:text-green-900 font-medium"
            >
              Vis alle
            </button>
          )}
        </div>
        {bikes.length === 0 ? (
          <div className="bg-white rounded-2xl border p-5 text-center text-gray-600 text-sm">
            Ingen sykler registrert
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {bikes.map((b) => (
                <BikeCard
                  key={b._id}
                  bike={b}
                  customer={customer}
                  selected={selectedBikeIds.has(String(b._id))}
                  onToggle={() => toggleBike(String(b._id))}
                />
              ))}
            </div>
            {bikes.length > 1 && (
              <p className="text-xs text-gray-600 text-center mt-2" role="status">
                {selectedBikeIds.size === 0
                  ? 'Trykk på en sykkel for å filtrere historikk'
                  : `Viser historikk for ${selectedBikeIds.size} av ${bikes.length} sykler`}
              </p>
            )}
          </>
        )}
      </section>

      {dekkhotell.length > 0 && <Dekkhotell poster={dekkhotell} />}
      {sykkelhotell.length > 0 && <Sykkelhotell poster={sykkelhotell} />}

      {/* Servicehistorikk */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 px-1">
          Servicehistorikk ({filteredHistory.length}{selectedBikeIds.size > 0 && history.length !== filteredHistory.length ? ` av ${history.length}` : ''})
        </h2>
        {filteredHistory.length === 0 ? (
          <div className="bg-white rounded-2xl border p-5 text-center text-gray-600 text-sm">
            {selectedBikeIds.size > 0 ? 'Ingen servicehistorikk for valgte sykler' : 'Ingen servicehistorikk'}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredHistory.map((svc) => {
              const statusLabel = STATUS_LABELS[svc.status] || svc.status || '';
              const statusColor = STATUS_COLORS[svc.status] || 'bg-gray-100 text-gray-600';
              const desc = serviceDesc(svc);
              const total = serviceTotal(svc);
              const bike = bikes.find((b) => String(b._id) === String(svc.bikeId));
              const svcLines = Array.isArray(svc.serviceLines) ? svc.serviceLines : [];
              const prtLines = Array.isArray(svc.partsLines) ? svc.partsLines : [];
              const statusLow = String(svc.status || '').toLowerCase();
              const isAvsluttet = statusLow === 'avsluttet';
              const isOpen = statusLow !== 'avsluttet' && statusLow !== 'kansellert';
              const hasReceipt = isAvsluttet && (svcLines.length > 0 || prtLines.length > 0);
              const isExpanded = expandedSvcIds.has(String(svc._id));

              const svcSum = svcLines.reduce((s, l) => (l.reklamasjon || l.gratisarbeid ? s : s + (Number(l.pris) || 0)), 0);
              const prtSum = prtLines.reduce((s, l) => {
                if (l.isWarranty) return s;
                return s + Number(l.price ?? l.pris ?? 0) * Number(l.qty ?? l.antall ?? 1) - Number(l.discount ?? 0);
              }, 0);
              const grandTotal = svcSum + prtSum;

              const cardContent = (
                <>
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {svc.serviceRef && (
                        <span className="font-mono text-xs text-gray-600">#{svc.serviceRef}</span>
                      )}
                      {statusLabel && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
                          {statusLabel}
                        </span>
                      )}
                      {isOpen && svc.portal?.token && (
                        <span className="text-xs text-green-700 font-medium">Åpne<span aria-hidden="true"> →</span></span>
                      )}
                    </div>
                    <span className="text-xs text-gray-600 flex-shrink-0">{fmtDate(svc.createdAt)}</span>
                  </div>

                  {bike && selectedBikeIds.size !== 1 && (
                    <div className="text-xs text-gray-500 mb-1">
                      {[bike.merke, bike.modell].filter(Boolean).join(' ')}
                    </div>
                  )}
                  {desc && <div className="text-sm text-gray-700 mt-1">{desc}</div>}
                  {!isExpanded && total > 0 && (
                    <div className="text-sm font-semibold text-gray-900 mt-1">{fmtNOK(total)}</div>
                  )}

                  {/* Vis kvittering toggle */}
                  {hasReceipt && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <label className="flex items-center gap-2 cursor-pointer select-none" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isExpanded}
                          onChange={() => toggleReceipt(String(svc._id))}
                          className="w-4 h-4 rounded border-gray-500 text-green-700 focus:ring-green-700 cursor-pointer"
                        />
                        <span className="text-xs text-gray-600">
                          Vis kvittering
                          {svc.serviceRef && <span className="sr-only"> for service #{svc.serviceRef}</span>}
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Kvittering */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      {svc.arbeidsbeskrivelse?.trim() && (
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Arbeidsbeskrivelse</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{svc.arbeidsbeskrivelse.trim()}</p>
                        </div>
                      )}

                      {svcLines.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Arbeid</p>
                          <ul className="divide-y divide-gray-100">
                            {svcLines.map((l, i) => (
                              <li key={l._id || i} className="py-2 flex justify-between gap-3 text-sm">
                                <div className="min-w-0">
                                  <div className="font-medium">{l.navn || 'Tjeneste'}</div>
                                  {l.kommentar && <div className="text-xs text-gray-500">{l.kommentar}</div>}
                                </div>
                                <div className="shrink-0 text-right">
                                  {l.reklamasjon ? (
                                    <>
                                      <div className="font-medium text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(l.pris)}</div>
                                      <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Reklamasjon</span>
                                    </>
                                  ) : l.gratisarbeid ? (
                                    <>
                                      <div className="font-medium text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(l.pris)}</div>
                                      <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">Gratisarbeid</span>
                                    </>
                                  ) : (
                                    <div className="font-medium whitespace-nowrap">{fmtNOK(l.pris)}</div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                          {prtLines.length > 0 && (
                            <div className="flex justify-between text-xs text-gray-500 pt-1">
                              <span>Sum arbeid</span><span>{fmtNOK(svcSum)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {prtLines.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Deler</p>
                          <ul className="divide-y divide-gray-100">
                            {prtLines.map((p, i) => {
                              const price = Number(p.price ?? p.pris ?? 0);
                              const qty = Math.max(1, Number(p.qty ?? p.antall ?? 1));
                              const discount = Math.max(0, Number(p.discount ?? 0));
                              const line = Math.max(0, price * qty - discount);
                              return (
                                <li key={i} className="py-2 flex justify-between gap-3 text-sm">
                                  <div className="min-w-0">
                                    <div className="font-medium">{p.product || p.navn || 'Del'}{p.brand ? ` (${p.brand})` : ''}</div>
                                    <div className="text-xs text-gray-500">{qty} stk · {fmtNOK(price)}</div>
                                  </div>
                                  <div className="shrink-0 text-right">
                                    {p.isWarranty ? (
                                      <>
                                        <div className="font-medium text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(line)}</div>
                                        <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Reklamasjon</span>
                                      </>
                                    ) : (
                                      <div className="font-medium whitespace-nowrap">{fmtNOK(line)}</div>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                          {svcLines.length > 0 && (
                            <div className="flex justify-between text-xs text-gray-500 pt-1">
                              <span>Sum deler</span><span>{fmtNOK(prtSum)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex justify-between items-baseline pt-2 border-t border-gray-200">
                        <span className="text-sm font-semibold text-gray-800">Totalt</span>
                        <span className="text-base font-bold text-gray-900">{fmtNOK(grandTotal)}</span>
                      </div>
                    </div>
                  )}
                </>
              );

              return isOpen && svc.portal?.token ? (
                <a key={svc._id} href={`/svc/${svc.portal?.token}?back=min-side`}
                  className="block bg-white rounded-xl border shadow-sm p-4 hover:border-green-400 hover:shadow-md transition-all">
                  {cardContent}
                </a>
              ) : (
                <div key={svc._id} className="bg-white rounded-xl border shadow-sm p-4">
                  {cardContent}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <DineData token={token} apiBase={API_BASE_URL} apen={visData} onNyInnlogging={onNyInnlogging} />
    </div>
  );
}

// ─── Hoved-komponent ──────────────────────────────────────────────────────────
export default function CustomerPortal() {
  const [step, setStep] = useState('request'); // 'request' | 'verify' | 'dashboard'
  const [pinData, setPinData] = useState(null); // { identifier, method, masked }
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  // Satt når kunden selv har byttet steg — da flyttes fokus til det nye steget
  const [byttet, setByttet] = useState(false);
  // Fra lenken butikken sender (/min-side?vis=mine-data, src/gdpr/innsyn.js): åpne «Dine data».
  const [visData, setVisData] = useState(() => new URLSearchParams(window.location.search).get('vis') === 'mine-data');
  // Beskjed på innloggingssiden, f.eks. etter at kunden har byttet telefonnummer
  const [melding, setMelding] = useState(() =>
    visData && !sessionStorage.getItem(TOKEN_KEY) ? 'Logg inn for å se opplysningene vi har om deg.' : ''
  );

  // På butikkdomenene (tilbud.sport1royken.no o.l.) er butikken avsender: Sport 1-navn og -logo.
  const butikkdomene = kundedomene();
  const merkeNavn = butikkdomene?.navn || getDisplayName();
  useSideTittel(`Din side – ${merkeNavn}`);

  useEffect(() => {
    if (token) setStep('dashboard');
  }, [token]);

  const handlePinRequested = (data) => {
    setMelding('');
    setByttet(true);
    setPinData(data);
    setStep('verify');
  };

  const handleVerified = (jwt) => {
    setByttet(true);
    sessionStorage.setItem(TOKEN_KEY, jwt);
    setToken(jwt);
    setStep('dashboard');
  };

  const handleLogout = (beskjed) => {
    setMelding(typeof beskjed === 'string' ? beskjed : '');
    setByttet(true);
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setPinData(null);
    setStep('request');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b-4 border-red-600 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <img
            src={butikkdomene?.logo || '/images/RoykenSykkelservice_logo_transparent.png'}
            alt={merkeNavn}
            className="h-12 w-auto object-contain"
          />
          <div className="text-right">
            {step === 'dashboard' && (
              <div className="text-xs text-gray-600 uppercase tracking-wide font-medium">Innlogget</div>
            )}
            <h1 className="font-bold text-gray-900 text-base leading-tight">Din side</h1>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {step === 'request' && melding && (
          <p className="bg-green-50 border border-green-200 text-green-900 rounded-xl p-4 text-sm mb-4" role="status">
            {melding}
          </p>
        )}
        {step === 'request' && <RequestPinStep onSuccess={handlePinRequested} fokus={byttet} />}
        {step === 'verify' && pinData && (
          <VerifyPinStep
            identifier={pinData.identifier}
            customerId={pinData.customerId}
            method={pinData.method}
            masked={pinData.masked}
            onSuccess={handleVerified}
            onBack={() => { setByttet(true); setStep('request'); }}
          />
        )}
        {step === 'dashboard' && token && (
          <Dashboard
            token={token}
            onLogout={handleLogout}
            fokus={byttet}
            visData={visData}
            onNyInnlogging={() => {
              setVisData(true);
              handleLogout('Logg inn på nytt for å laste ned eller sende dataene dine.');
            }}
          />
        )}
      </div>
    </div>
  );
}
